import { useQueryClient } from '@tanstack/react-query';
import { toastifyToast } from 'component-library';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetDraftPlotTaskStatus } from '@/api/rest/resources/plot';
import { Logger } from '@/lib/logs/logger';

const COMMIT_TOAST_ID = 'draft-plot-commit';

type UseCommitTaskPollerParams = {
  taskId: string | null;
  count: number;
};

export const useCommitTaskPoller = ({ taskId, count }: UseCommitTaskPollerParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const completedTaskId = useRef<string | null>(null);

  const { data } = useGetDraftPlotTaskStatus(
    { queryParams: { task_id: taskId ?? '' } },
    {
      enabled: !!taskId,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status === 'SUCCESS' || status === 'FAILED') return false;
        return 2_000;
      },
    },
  );

  useEffect(() => {
    if (!data) return;

    if (data.status === 'SUCCESS') {
      if (completedTaskId.current === taskId) return;
      completedTaskId.current = taskId;
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['land_steward', 'draftplot'] }),
        queryClient.invalidateQueries({ queryKey: ['land_steward', 'draftplotgroup'] }),
        queryClient.invalidateQueries({ queryKey: ['plots'] }),
      ]).catch((error: unknown) => Logger.errorOnce(error as Error));
      const retainedCount = data.plots_retained_as_draft ?? 0;
      toastifyToast.update(COMMIT_TOAST_ID, {
        render:
          retainedCount > 0
            ? t('shared.uploadPlots.commit.successWithRetained', {
                createdCount: data.plots_created,
                retainedCount,
              })
            : t('shared.uploadPlots.commit.success', { count: data.plots_created ?? count }),
        type: retainedCount > 0 ? 'default' : 'success',
        autoClose: retainedCount > 0 ? 10_000 : 6_000,
      });
    } else if (data.status === 'FAILED') {
      toastifyToast.update(COMMIT_TOAST_ID, {
        render: data.error_message ?? t('shared.uploadPlots.commit.failed'),
        type: 'error',
        autoClose: 10_000,
      });
    }
  }, [data, count, queryClient, t, taskId]);
};
